import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type BucketPillButtonProps = Omit<
  React.ComponentProps<typeof Button>,
  "variant" | "size"
> & {
  active?: boolean;
  showViolation?: boolean;
  /** 있으면 호버 시 툴팁 (shadcn 기본 스타일) */
  tooltip?: string;
};

export function BucketPillButton({
  active = false,
  disabled = false,
  showViolation = false,
  tooltip,
  className,
  children,
  ...props
}: BucketPillButtonProps) {
  const tip = tooltip?.trim();
  const [tipTitle, ...tipRest] = (tip ?? "").split("\n");
  const tipBody = tipRest.join("\n").trim();

  const button = (
    <Button
      variant="outline"
      size="lg"
      disabled={disabled}
      aria-invalid={showViolation || undefined}
      className={cn(
        "h-auto flex-1 max-w-full shrink-0 justify-between px-3 py-2 font-pretendard text-sm font-normal",
        "text-foreground hover:text-foreground",
        disabled &&
          "!cursor-not-allowed disabled:!cursor-not-allowed disabled:!pointer-events-auto disabled:opacity-50",
        showViolation &&
          !active &&
          "border-destructive text-destructive hover:border-destructive hover:bg-destructive/5 hover:text-destructive",
        active &&
          !showViolation &&
          "border-sig bg-[#0E59C6]/5 text-sig hover:border-sig hover:bg-sig/10 hover:text-sig",
        active &&
          showViolation &&
          "border-sig! bg-destructive/5 text-destructive hover:border-sig hover:bg-destructive/10 hover:text-destructive",
        className
      )}
      {...props}
    >
      {children}
    </Button>
  );

  if (!tip) {
    return button;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <TooltipPrimitive.Root>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent
          side="top"
          sideOffset={6}
          className="rounded-lg bg-[#FB2C36] px-3 py-1.5 text-xs text-white"
          arrowClassName="fill-[#FB2C36]"
        >
          <div className="whitespace-pre-line">
            <p className="text-xs font-semibold leading-tight mb-1">
              {tipTitle}
            </p>
            <div className="flex items-center">
              <div className="size-1 bg-white/70 ml-1 rounded-full" />{" "}
              {tipBody && <p className="ml-[2px] ">{tipBody}</p>}
            </div>
          </div>
        </TooltipContent>
      </TooltipPrimitive.Root>
    </TooltipProvider>
  );
}
